import React, { useEffect, useState } from "react";
import parties from "../data/party";
import categoriesApi from "../data/categories";
import { fetchStocknames } from "../data/Stocknames";
import stockdata from "../data/stock";
import Select from "react-select";

interface Party {
  id: number;
  date: string;
  name: string;
  number: string;
  contact_person_name: string;
  contact_person_number: string;
  categories: string[];
  deleteparty: number;
  total_amount?: number | string;
}

interface CategoryApiType {
  id: number | string;
  category_name: string;
}

interface StockNames {
  id: number;
  name: string;
  [key: string]: any;
}

type StockDBCategoryItem = {
  id: number | string;
  investment_type: string;
  category: string;
  stock_name: string;
  unit: string;
  total_amount: string;
  indirect_charges: string;
  perUnitCharge: string;
  [key: string]: any;
};

type PartyStockDBType = {
  id: number | string;
  date: string;
  party_id: number | string;
  categories: string | StockDBCategoryItem[];
  [key: string]: any;
};

function EditStockPage() {
  const [partiesList, setPartiesList] = useState<Party[]>([]);
  const [categoriesFromApi, setCategoriesFromApi] = useState<CategoryApiType[]>([]);
  const [stocksnames, setStocksnames] = useState<StockNames[]>([]);
  const [fetching, setFetching] = useState(true);

  // URL params: party_id, category, stock_id
  const [params, setParams] = useState<{ party_id: string; category: string; stock_id: string }>({
    party_id: "",
    category: "",
    stock_id: "",
  });

  // Edit form state
  const [form, setForm] = useState<{
    party_id: string;
    category: string;
    stock_id: string;
    stock_name: string;
    unit: string;
    total_amount: string;
    indirect_charges: string;
    perUnitCharge: string;
    date: string;
  }>({
    party_id: "",
    category: "",
    stock_id: "",
    stock_name: "",
    unit: "",
    total_amount: "",
    indirect_charges: "",
    perUnitCharge: "",
    date: "",
  });

  const [loading, setLoading] = useState(false);

  // Go Back function
  const goBack = () => {
    if (window.history.length > 1) {
      window.history.back();
      setTimeout(() => window.location.reload(), 250);
    } else {
      window.location.href = "/all-stock";
    }
  };

  // Fetch essentials for select fields
  useEffect(() => {
    (async function () {
      setFetching(true);
      try {
        const [myParties, catsRes, stocksnamesRes] = await Promise.all([
          parties(),
          categoriesApi(),
          fetchStocknames(),
        ]);
        setPartiesList(Array.isArray(myParties) ? myParties : []);
        setCategoriesFromApi(Array.isArray(catsRes) ? catsRes : []);
        setStocksnames(Array.isArray(stocksnamesRes) ? stocksnamesRes : []);
      } catch {
        setPartiesList([]);
        setCategoriesFromApi([]);
        setStocksnames([]);
      }
      setFetching(false);
    })();
  }, []);

  // Parse query params
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const party_id = urlParams.get("party_id") ?? "";
    const category = urlParams.get("category") ?? "";
    const stock_id = urlParams.get("stock_id") ?? "";
    setParams({ party_id, category, stock_id });
  }, []);

  // Fetch form data from DB & assign to state
  useEffect(() => {
    if (!params.party_id || !params.category || !params.stock_id) return;

    (async () => {
      const dbRows: PartyStockDBType[] = await stockdata();
      const stockRow = dbRows.find((row) => String(row.id) === String(params.stock_id));
      if (!stockRow) {
        setForm((f) => ({
          ...f,
          party_id: params.party_id,
          category: params.category,
          stock_id: params.stock_id,
        }));
        return;
      }

      // Parse categories from string to array
      let categoriesArr: StockDBCategoryItem[] = [];
      if (typeof stockRow.categories === "string") {
        try {
          const arr = JSON.parse(stockRow.categories);
          if (Array.isArray(arr)) categoriesArr = arr;
        } catch {}
      } else if (Array.isArray(stockRow.categories)) {
        categoriesArr = stockRow.categories as StockDBCategoryItem[];
      }

      // Find correct category in row
      let stock: StockDBCategoryItem | undefined =
        categoriesArr.find(
          (cat) =>
            String(cat.id) === String(params.category) ||
            String(cat.category) === String(params.category) ||
            String(cat.category) === String(Number(params.category))
        ) ||
        (categoriesArr.length > 0 ? categoriesArr[0] : undefined);

      setForm((prev) => ({
        ...prev,
        party_id: String(stockRow.party_id ?? params.party_id),
        category: stock?.category ?? params.category,
        stock_id: String(stockRow.id ?? params.stock_id),
        stock_name: stock?.stock_name ?? "",
        unit: stock?.unit ?? "",
        total_amount: stock?.total_amount ?? "",
        indirect_charges: stock?.indirect_charges ?? "",
        perUnitCharge: stock?.perUnitCharge ?? "",
        date: stockRow.date
          ? stockRow.date.length > 10
            ? stockRow.date.substr(0, 10)
            : stockRow.date
          : "",
      }));
    })();
  }, [params.party_id, params.category, params.stock_id]);

  // Input changed
  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setForm((f) => ({
      ...f,
      [name]: value,
    }));
    // Per unit calculation logic
    if (["unit", "total_amount", "indirect_charges"].includes(name)) {
      let unitNum = parseFloat(name === "unit" ? value : form.unit || "0");
      let totalAmountNum = parseFloat(name === "total_amount" ? value : form.total_amount || "0");
      let indirectNum = parseFloat(name === "indirect_charges" ? value : form.indirect_charges || "0");
      const perUnit = unitNum > 0 ? ((totalAmountNum + indirectNum) / unitNum).toFixed(2) : "";
      setForm((f) => ({ ...f, perUnitCharge: perUnit }));
    }
  };

  const handleStockNameSelect = (selected: any) => {
    setForm((f) => ({
      ...f,
      stock_name: selected?.value || "",
    }));
  };

  // Submit logic
  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!form.party_id) return alert("No party selected");
    if (!form.category) return alert("No category selected");
    if (!form.stock_name) return alert("No stock name selected");
    if (!form.unit || isNaN(+form.unit) || +form.unit <= 0)
      return alert("Please enter a valid unit (number)");
    if (!form.total_amount || isNaN(+form.total_amount))
      return alert("Please enter valid total amount");
    if (!form.date) return alert("Please enter Date.");
    setLoading(true);

    const categoriesArr: StockDBCategoryItem[] = [
      {
        id: form.category,
        investment_type: "structure",
        category: form.category,
        stock_name: form.stock_name,
        unit: form.unit,
        total_amount: form.total_amount,
        indirect_charges: form.indirect_charges || "0",
        perUnitCharge: form.perUnitCharge || "0",
      },
    ];

    try {
      const body = {
        id: form.stock_id,
        party_id: form.party_id,
        categories: JSON.stringify(categoriesArr),
        category: form.category,
        name: form.stock_name,
        unit: form.unit,
        price_per_unit: form.perUnitCharge,
        total_amount: form.total_amount,
        indirect_charges: form.indirect_charges || "0",
        date: form.date,
      };
      const url = `${process.env.REACT_APP_API_URL}/auth/addstocks`;
      const res = await fetch(url, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        let ress;
        try {
          ress = await res.json();
        } catch {}
        throw new Error(ress?.message || "Failed to update");
      }
      alert("Stock updated successfully.");
      setTimeout(() => {
        goBack();
      }, 200);
    } catch (err: any) {
      alert(err?.message || "Error while updating");
    } finally {
      setLoading(false);
    }
  };

  const availableParty = partiesList.find((p) => String(p.id) === String(params.party_id));
  const availableCategory = categoriesFromApi.find(
    (cat) =>
      String(cat.id) === String(form.category) ||
      String(cat.category_name).toLowerCase() === String(form.category).toLowerCase()
  );

  if (fetching)
    return (
      <div className="edit-sell__loading-box">
        <div
          style={{
            marginTop: 32,
            fontWeight: 500,
            fontSize: 18,
            color: "#8c8c8c",
            textAlign: "center",
          }}
        >
          Loading...
        </div>
      </div>
    );

  if (!params.party_id || !params.category || !params.stock_id) {
    return (
      <div
        className="edit-sell__invalid-url-error"
        style={{
          marginTop: 34,
          fontWeight: 700,
          fontSize: 18,
          color: "#e62d32",
          textAlign: "center",
        }}
      >
        Invalid URL. Please supply <code>party_id</code>, <code>category</code>, and <code>stock_id</code> in query string.
        <br />
        Example: <code>/edit-stock/?party_id=29&amp;category=2&amp;stock_id=137</code>
      </div>
    );
  }

  // --------- UI LAYOUT & MULTI ROW FIELDS ---------
  return (
    <div
      className="edit-sell__modal-box"
      style={{
        maxWidth: 570,
        margin: "44px auto",
        background: "#fff",
        borderRadius: 16,
        boxShadow: "0 8px 46px #1cac701a, 0 2px 12px #ff95001a",
        border: "1px solid #f3f3f3",
        overflow: "hidden",
        padding: 0,
      }}
    >
      {/* Header */}
      <div
        className="edit-sell__header"
        style={{
          padding: 0,
          height: 54,
          borderBottom: "1px solid #f1efef",
          background: "linear-gradient(90deg,#fafdff 65%,#fff8f1 100%)",
          display: "flex",
          alignItems: "center",
          gap: 16,
          position: "relative",
        }}
      >
        <button
          type="button"
          onClick={goBack}
          aria-label="Back"
          style={{
            height: 46,
            width: 46,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "none",
            outline: "none",
            border: 0,
            marginLeft: 10,
            marginRight: 0,
            cursor: "pointer",
          }}
        >
          <svg width={29} height={29} fill="none" stroke="#1cac70" strokeWidth="2.1" viewBox="0 0 24 24">
            <path d="M15 18l-6-6 6-6" strokeLinecap="round" strokeLinejoin="round"></path>
          </svg>
        </button>
        <div
          style={{
            fontWeight: 800,
            fontSize: 21,
            color: "#383838",
            marginLeft: 2,
            letterSpacing: 0.1,
          }}
        >
          Edit Stock
        </div>
      </div>
      <form
        onSubmit={handleSubmit}
        className="edit-sell-form"
        autoComplete="off"
        style={{
          padding: "26px 32px 13px 32px",
          display: "flex",
          flexDirection: "column",
          gap: 16,
          background: "linear-gradient(94deg,#fbfeff 60%,#fff8f1 100%)",
        }}
      >
        {/* Party & Category in a row */}
        <div style={{ display: "flex", gap: 12 }}>
          <div style={{ flex: 1 }}>
            <label
              style={{
                fontWeight: 600,
                fontSize: 15,
                marginBottom: 7,
                color: "#256146",
                letterSpacing: 0.08,
                display: "block",
              }}
            >
              Party
            </label>
            <input
              readOnly
              disabled
              value={availableParty ? availableParty.name : params.party_id}
              style={{
                width: "100%",
                background: "#f5faf8",
                border: "1.4px solid #e7ece9",
                borderRadius: 8,
                color: "#90a897",
                height: 44,
                padding: "10px 14px",
                fontWeight: 700,
                fontSize: 15,
                letterSpacing: 0.18,
                outline: 0,
                margin: 0,
              }}
            />
          </div>
          <div style={{ flex: 1 }}>
            <label
              style={{
                fontWeight: 600,
                fontSize: 15,
                marginBottom: 7,
                color: "#a16017",
                display: "block",
                letterSpacing: 0.07,
              }}
            >
              Category
            </label>
            <input
              readOnly
              disabled
              value={availableCategory ? availableCategory.category_name : form.category}
              style={{
                width: "100%",
                background: "#fff3e6",
                border: "1.4px solid #f3e5cb",
                borderRadius: 8,
                color: "#d49f47",
                height: 44,
                padding: "10px 14px",
                fontWeight: 700,
                fontSize: 15,
                letterSpacing: 0.18,
                outline: 0,
                margin: 0,
              }}
            />
          </div>
        </div>
        {/* Stock Name & Unit in a row */}
        <div style={{ display: "flex", gap: 12 }}>
          <div style={{ flex: 2.05 }}>
            <label
              style={{
                fontWeight: 700,
                fontSize: 15,
                marginBottom: 7,
                color: "#44378a",
                display: "block",
                letterSpacing: 0.09,
              }}
            >
              Stock Name
            </label>
            <Select
              options={stocksnames.map((item) => ({
                value: item.name,
                label: item.name,
              }))}
              value={form.stock_name ? { value: form.stock_name, label: form.stock_name } : null}
              onChange={handleStockNameSelect}
              placeholder="Select stock name"
              styles={{
                control: (base, state) => ({
                  ...base,
                  borderColor: state.isFocused ? "#6858e4" : "#ebeef8",
                  minHeight: "44px",
                  boxShadow: "none",
                  fontSize: 15,
                  fontWeight: 600,
                  borderRadius: 8,
                  background: "#f8f6fc",
                  color: "#31277e",
                }),
                menu: (base) => ({
                  ...base,
                  zIndex: 31,
                }),
                option: (base, state) => ({
                  ...base,
                  background: state.isFocused ? "#f2ecfd" : "#fff",
                  color: "#2b2476",
                  fontWeight: state.isFocused ? 700 : 500,
                }),
              }}
              isClearable
            />
          </div>
          <div style={{ flex: 1 }}>
            <label
              style={{
                fontWeight: 600,
                fontSize: 15,
                marginBottom: 7,
                color: "#256146",
                display: "block",
                letterSpacing: 0.08,
              }}
            >
              Unit
            </label>
            <input
              type="number"
              step="any"
              name="unit"
              min="0"
              required
              value={form.unit}
              onChange={handleFormChange}
              placeholder="Units"
              style={{
                width: "100%",
                border: "1.4px solid #ebeef8",
                background: "#fafdff",
                borderRadius: 8,
                color: "#246a59",
                height: 44,
                padding: "10px 14px",
                fontWeight: 700,
                fontSize: 15,
                letterSpacing: 0.15,
                outline: "none",
              }}
            />
          </div>
        </div>
        {/* Total Amount & Indirect Charges in a row */}
        <div style={{ display: "flex", gap: 12 }}>
          <div style={{ flex: 1 }}>
            <label
              style={{
                fontWeight: 600,
                fontSize: 15,
                marginBottom: 7,
                color: "#a16017",
                display: "block",
                letterSpacing: 0.09,
              }}
            >
              Total Amount
            </label>
            <input
              type="number"
              step="any"
              name="total_amount"
              min="0"
              required
              value={form.total_amount}
              onChange={handleFormChange}
              placeholder="Total Amount"
              style={{
                width: "100%",
                border: "1.4px solid #f3e5cb",
                background: "#fff8f2",
                borderRadius: 8,
                color: "#a16017",
                height: 44,
                padding: "10px 14px",
                fontWeight: 700,
                fontSize: 15,
                letterSpacing: 0.15,
                outline: "none",
              }}
            />
          </div>
          <div style={{ flex: 1 }}>
            <label
              style={{
                fontWeight: 600,
                fontSize: 15,
                marginBottom: 7,
                color: "#895629",
                display: "block",
                letterSpacing: 0.09,
              }}
            >
              Indirect Charges
            </label>
            <input
              type="number"
              step="any"
              name="indirect_charges"
              min="0"
              value={form.indirect_charges}
              onChange={handleFormChange}
              placeholder="Indirect Charges"
              style={{
                width: "100%",
                border: "1.4px solid #f5e8da",
                background: "#fbf7f3",
                borderRadius: 8,
                color: "#895629",
                height: 44,
                padding: "10px 14px",
                fontWeight: 700,
                fontSize: 15,
                letterSpacing: 0.15,
                outline: "none",
              }}
            />
          </div>
        </div>
        {/* Per Unit Charge & Date in a row */}
        <div style={{ display: "flex", gap: 12 }}>
          <div style={{ flex: 1 }}>
            <label
              style={{
                fontWeight: 600,
                fontSize: 15,
                marginBottom: 7,
                color: "#864f09",
                display: "block",
                letterSpacing: 0.09,
              }}
            >
              Per Unit Charge
            </label>
            <input
              type="number"
              step="any"
              name="perUnitCharge"
              value={form.perUnitCharge}
              onChange={handleFormChange}
              readOnly
              placeholder="Auto"
              style={{
                width: "100%",
                border: "1.4px solid #e8ecf3",
                background: "#fffcf6",
                borderRadius: 8,
                color: "#d09f39",
                height: 44,
                padding: "10px 14px",
                fontWeight: 700,
                fontSize: 15,
                letterSpacing: 0.15,
                outline: "none",
              }}
            />
          </div>
          <div style={{ flex: 1 }}>
            <label
              style={{
                fontWeight: 600,
                fontSize: 15,
                marginBottom: 7,
                color: "#256146",
                display: "block",
                letterSpacing: 0.09,
              }}
            >
              Date
            </label>
            <input
              type="date"
              name="date"
              required
              value={form.date}
              onChange={handleFormChange}
              placeholder="Date"
              style={{
                width: "100%",
                border: "1.4px solid #e1ece7",
                background: "#f7fbfa",
                borderRadius: 8,
                color: "#1cac70",
                height: 44,
                padding: "10px 14px",
                fontWeight: 700,
                fontSize: 15,
                letterSpacing: 0.15,
                outline: "none",
              }}
            />
          </div>
        </div>
        <button
          type="submit"
          disabled={loading}
          style={{
            width: "100%",
            padding: "12px 0",
            marginTop: 8,
            background: "linear-gradient(90deg,#1cac70 0%,#ff9500 100%)",
            color: "#fff",
            fontWeight: 800,
            border: "none",
            borderRadius: 13,
            fontSize: 19,
            letterSpacing: 0.13,
            boxShadow: "0 1px 8px #ff950073, 0 2px 12px #1cac7044",
            cursor: loading ? "not-allowed" : "pointer",
            transition: "background 0.24s",
          }}
        >
          {loading ? "Updating..." : "Update"}
        </button>
        <button
          type="button"
          className="edit-sell__prev-btn"
          onClick={goBack}
          style={{
            width: "100%",
            padding: "11px 0",
            marginTop: 5,
            background: "#fff",
            color: "#1cac70",
            border: "1.5px solid #1cac70",
            borderRadius: 13,
            fontSize: 17,
            fontWeight: 700,
            letterSpacing: 0.10,
            boxShadow: "none",
            marginBottom: 1,
            cursor: "pointer",
            transition: "all 0.21s",
          }}
        >
          Previous
        </button>
      </form>
    </div>
  );
}

export default EditStockPage;
